import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Personal access tokens: bearer secrets an employee makes for scripts and Claude (MCP), each
 * acting as that employee. `Authorization: Bearer <token>` works on every admin endpoint. An
 * employee sees and revokes only their own. Making one needs a browser session, so a token cannot
 * make more tokens.
 */

/** Every token starts with this, so a leaked one is recognisable (and secret scanners can find it). */
export const TOKEN_PREFIX = "cbpat_";

export const PersonalAccessToken = z.object({
	id: z.string(),
	name: z.string(),
	/** The token's last four characters: `cbpat_…k3Xa`. */
	hint: z.string(),
	createdAt: z.iso.datetime(),
	/** When it last authenticated a request (to the minute), ISO 8601. */
	lastUsedAt: z.iso.datetime().nullable(),
});
export type PersonalAccessToken = z.infer<typeof PersonalAccessToken>;

export const CreatePersonalAccessTokenInput = z.object({
	name: z.string().trim().min(1).max(100),
});
export type CreatePersonalAccessTokenInput = z.infer<typeof CreatePersonalAccessTokenInput>;

/** The new token, and the secret itself: shown once, never again. */
export const CreatedPersonalAccessToken = z.object({
	token: PersonalAccessToken,
	secret: z.string(),
});
export type CreatedPersonalAccessToken = z.infer<typeof CreatedPersonalAccessToken>;

export const PersonalAccessTokenIdInput = z.object({ id: z.string().max(64) });

export const PersonalAccessTokenContract = {
	/** The signed-in employee's tokens that still work, newest first. */
	list: oc.route({ method: "GET", path: "/admin/tokens" }).output(z.array(PersonalAccessToken)),
	/** A new token; 403 `SessionRequired` when asked with a token instead of a browser session. */
	create: oc
		.route({ method: "POST", path: "/admin/tokens" })
		.input(CreatePersonalAccessTokenInput)
		.output(CreatedPersonalAccessToken),
	/** Revokes one of your own tokens; 404 for anyone else's or one already revoked. */
	revoke: oc.route({ method: "DELETE", path: "/admin/tokens/{id}" }).input(PersonalAccessTokenIdInput),
};
